import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { allPages } from "@/lib/pagination";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const WorkspaceContext = createContext(null);
export const useWorkspace = () => useContext(WorkspaceContext);

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const activeOrg = useRef("");
  const generation = useRef(0);
  const projectRequest = useRef(0);

  const selectProject = useCallback((pid) => {
    setProjectId(pid);
    localStorage.setItem("projectId", pid);
  }, []);

  const loadOrgs = useCallback(async () => {
    const data = await allPages("/orgs");
    setOrgs(data);
    return data;
  }, []);

  const loadProjects = useCallback(async (oid) => {
    if (!oid || oid !== activeOrg.current) return [];
    const request = ++projectRequest.current;
    const data = await allPages(`/orgs/${oid}/projects`);
    if (oid === activeOrg.current && request === projectRequest.current) {
      setProjects(data);
      const saved = localStorage.getItem("projectId");
      selectProject(data.find((p) => p.id === saved)?.id || data[0]?.id || "");
    }
    return data;
  }, [selectProject]);

  useEffect(() => {
    const version = ++generation.current;
    activeOrg.current = "";
    setOrgId("");
    setOrgs([]);
    setProjects([]);
    setProjectId("");
    setError(null);
    setLoading(!!user);
    if (!user) return;
    (async () => {
      try {
        const os = await allPages("/orgs");
        if (version !== generation.current) return;
        setOrgs(os);
        const saved = localStorage.getItem("orgId");
        const oid = os.find((o) => o.id && o.id === saved)?.id || os.find((o) => o.id)?.id || "";
        activeOrg.current = oid;
        setOrgId(oid);
        localStorage.setItem("orgId", oid);
        if (oid) await loadProjects(oid);
        else selectProject("");
      } catch (e) {
        if (version === generation.current) setError(e);
      } finally {
        if (version === generation.current) setLoading(false);
      }
    })();
    return () => {
      // Invalidate the latest request, including any organization switch since setup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      generation.current++;
      activeOrg.current = "";
    };
  }, [user, loadProjects, selectProject]);

  const selectOrg = async (oid) => {
    if (!oid || !orgs.some((o) => o.id === oid)) return;
    const version = ++generation.current;
    activeOrg.current = oid;
    setOrgId(oid);
    setProjects([]);
    selectProject("");
    localStorage.setItem("orgId", oid);
    setLoading(true);
    setError(null);
    try { await loadProjects(oid); }
    catch (e) { if (version === generation.current) setError(e); }
    finally { if (version === generation.current) setLoading(false); }
  };

  const createOrg = async (name) => {
    const { data } = await api.post("/orgs", { name: name.trim() });
    const version = ++generation.current;
    setOrgs((items) => [...items, data]);
    activeOrg.current = data.id;
    setOrgId(data.id);
    localStorage.setItem("orgId", data.id);
    setProjects([]);
    selectProject("");
    setError(null);
    setLoading(true);
    try { await loadProjects(data.id); }
    catch (e) { if (version === generation.current) setError(e); }
    finally { if (version === generation.current) setLoading(false); }
    return data;
  };

  const currentOrg = orgs.find((o) => o.id === orgId);
  const currentProject = projects.find((p) => p.id === projectId);

  return (
    <WorkspaceContext.Provider value={{
      orgs, projects, orgId, projectId, currentOrg, currentProject, loading, error,
      selectOrg, selectProject, loadOrgs, loadProjects, createOrg,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
}
